import React, { useState, useEffect } from "react";
import { FiExternalLink } from "react-icons/fi";
import { getPaginatedResults, orderByDate } from "../../utils/sortFunctions";
import PaginationNav from "../PaginationNav/PaginationNav";
import SortControls from "../SortControls/SortControls";
import PillSortButton from "../PillInput/PillSortButton";
import Modal from "../Modal/Modal";
import Button from "../Button/Button";
import { FaFilter } from "react-icons/fa";
import AutoCompleteInput from "../AutoCompleteInput/AutoCompleteInput";
import Loader from "../Loader/Loader";

const NOPtable = ({ ...props }) => {
  const [orders, setOrders] = useState([]);
  const [currentPage, setPage] = useState(1);
  const [searchOrderBy, setSearchOrderBy] = useState("sortDate");
  const [searchOrderDirection, setSearchOrderDirection] = useState("desc");
  const [filteredList, setFilteredList] = useState([]);
  const [modalOpen, toggleModal] = useState(false);
  const [selectedVb, setSelectedVb] = useState(null);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // Fetch orders from API
  useEffect(() => {
    fetch("/api/builderRecentCharges")
      .then((res) => res.json())
      .then((data) => {
        // Normalize API data to match expected fields
        const normalized = data.map((entry) => ({
          ...entry,
          sortDate: entry.date || entry.DATE_ISSUED || "",
          vb: entry.vb || entry.CUSTOMER_VALUE || "",
          order: entry.order || entry.OBD_HEADER_VALUE || "",
          note: entry.note || entry.NOTE || "",
          link: entry.LICENCE_NUMBER ? `/profile/${entry.LICENCE_NUMBER}` : "",
        }));
        setOrders(normalized);
        setFilteredList(orderByDate(normalized, "sortDate"));
        setIsLoading(false);
      });
  }, []);

  // Get unique values for filters
  const uniqueVbs = ["All", ...new Set(orders.map((order) => order.vb))];
  const uniqueOrders = ["All", ...new Set(orders.map((order) => order.order))];

  const applySorting = () => {
    const sorted = [...filteredList].sort((a, b) => {
      if (searchOrderBy === "vb") {
        return searchOrderDirection === "asc"
          ? a.vb.localeCompare(b.vb)
          : b.vb.localeCompare(a.vb);
      } else if (searchOrderBy === "sortDate") {
        return searchOrderDirection === "asc"
          ? new Date(a.sortDate) - new Date(b.sortDate)
          : new Date(b.sortDate) - new Date(a.sortDate);
      } else if (searchOrderBy === "order") {
        return searchOrderDirection === "asc"
          ? a.order.localeCompare(b.order)
          : b.order.localeCompare(a.order);
      }
      return 0;
    });
    setFilteredList(sorted);
  };

  const applyFilters = (vbFilter, orderFilter) => {
    let filtered = orders;
    if (vbFilter && vbFilter !== "All") {
      filtered = filtered.filter((item) => item.vb.toLowerCase() === vbFilter.toLowerCase());
    }
    if (orderFilter && orderFilter !== "All") {
      filtered = filtered.filter((item) => item.order.toLowerCase() === orderFilter.toLowerCase());
    }
    setFilteredList(orderByDate(filtered, "sortDate"));
  };

  const setListOrder = (filterId) => {
    if (filterId === searchOrderBy && searchOrderDirection === "asc") {
      setSearchOrderDirection("desc");
    } else {
      setSearchOrderBy(filterId);
      setSearchOrderDirection("asc");
    }
  };

  useEffect(() => {
    applySorting();
    // eslint-disable-next-line
  }, [searchOrderBy, searchOrderDirection]);

  // Add a date formatting function
  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    if (isNaN(date)) return dateStr;
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return date.toLocaleDateString('en-US', options);
  };

  return (
    <>
      <div className="flexContainer mobileCol justifyBetween alignEnd">
        <SortControls>
          <PillSortButton
            id="sortDate"
            label="Date Issued"
            active={searchOrderBy === "sortDate"}
            order={searchOrderDirection}
            clickEvt={() => setListOrder("sortDate")}
          />
          <PillSortButton
            id="vb"
            label="Vendor/Builder"
            active={searchOrderBy === "vb"}
            order={searchOrderDirection}
            clickEvt={() => setListOrder("vb")}
          />
          <PillSortButton
            id="order"
            label="Notices/Orders"
            active={searchOrderBy === "order"}
            order={searchOrderDirection}
            clickEvt={() => setListOrder("order")}
          />
        </SortControls>

        <Button
          id="filterBtn"
          icon={<FaFilter />}
          size="sm"
          clickEvt={() => toggleModal(!modalOpen)}
          text="Search Result"
        />
      </div>

      {modalOpen && (
        <Modal closeEvt={() => toggleModal(!modalOpen)}>
          <div>
            <h2>Filter Options</h2>
            <div>
              <h4>Vendor/Builder</h4>
              <AutoCompleteInput
                options={uniqueVbs}
                selectedOption={selectedVb || "All"}
                onChange={(value) => {
                  setSelectedVb(value);
                  applyFilters(value, selectedOrder);
                }}
                value={selectedVb || "All"}
                placeholder="Select a Vendor/Builder"
              />
            </div>
            <div style={{ marginTop: `2rem` }}>
              <div style={{ marginBottom: `.5rem` }}>
                <span className="bold">Order</span>
              </div>
              <AutoCompleteInput
                options={uniqueOrders}
                selectedOption={selectedOrder || "All"}
                onChange={(value) => {
                  setSelectedOrder(value);
                  applyFilters(selectedVb, value);
                }}
                value={selectedOrder || "All"}
                placeholder="Select an Order"
              />
            </div>
          </div>
        </Modal>
      )}

      {isLoading ? (
        <Loader />
      ) : (
        <>
          <table style={{ marginBottom: `2rem` }}>
            <thead>
              <tr>
                <th>Vendor/Builder</th>
                <th>Notice/Order Issued</th>
                <th>Date Issued</th>
              </tr>
            </thead>
            <tbody>
              {getPaginatedResults(filteredList, currentPage).map((order, i) => (
                <tr key={`${i}`}>
                  <td>
                    <a href={order.link} title={order.vb}>
                      <FiExternalLink
                        style={{
                          verticalAlign: "center",
                          marginRight: ".5rem",
                          fontSize: `1.5rem`,
                        }}
                      />
                      <span>{order.vb}</span>
                    </a>
                  </td>
                  <td>
                    {order.order}
                    {order.VB_IN_COMPLIANCE === "true" && (
                      <>
                        <br />
                        <b>Licensee has since complied</b>
                      </>
                    )}
                  </td>
                  <td>{formatDate(order.date || order.sortDate)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {filteredList.length > 10 && (
            <PaginationNav
              currentPage={currentPage}
              clickEvt={setPage}
              resultsLength={filteredList.length}
            />
          )}
        </>
      )}
    </>
  );
};

export default NOPtable;
